import type {
  AgentUpdateChannel,
  AgentUpdateFailureCode,
  AgentUpdateLevel,
  AgentUpdateMode,
  AgentUpdatePhase,
  AgentUpdateStatus,
  PrintAgentEventType,
  PrintAgentRevokeReason,
  PrintJobErrorCode,
  PrintTicketKind,
  PrinterState,
  PrinterTransport,
  PrinterWidth,
} from './api';

/**
 * Rótulos e formatação da impressão no backoffice (change production-printing).
 *
 * Os textos seguem os do painel do lojista, para o suporte falar a mesma língua
 * do dono: "enviado à impressora", "impressora inacessível", "Direto", "Modo
 * driver". Moram fora dos componentes pelo mesmo motivo de `badge-labels.ts`:
 * função pura junto de componente quebra o fast refresh.
 */

/** O horário da loja: o cupom e o painel usam o fuso de Brasília (StoreClock). */
const STORE_TIME_ZONE = 'America/Sao_Paulo';

const whenFormat = new Intl.DateTimeFormat('pt-BR', {
  timeZone: STORE_TIME_ZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** "25/09/2026 às 22:10", no horário da loja. */
export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  const parts = whenFormat.formatToParts(new Date(iso));
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
  return `${part('day')}/${part('month')}/${part('year')} às ${part('hour')}:${part('minute')}`;
}

/** "há 2 h", "há 5 min", "há 3 dias": para o último contato do computador. */
export function formatSince(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return 'nunca';
  const seconds = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return 'agora há pouco';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} h`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'há 1 dia' : `há ${days} dias`;
}

export function revokeReasonLabel(reason: PrintAgentRevokeReason | null): string {
  switch (reason) {
    case 'manual':
      return 'revogado na aba Impressão';
    case 'credential_reuse':
      return 'uso indevido da credencial';
    case 'store_deactivated':
      return 'a loja desativou a impressão';
    default:
      return 'motivo não registrado';
  }
}

export function transportLabel(transport: PrinterTransport): string {
  switch (transport) {
    case 'spooler':
      return 'Direto';
    case 'network':
      return 'Rede';
    case 'driver':
      return 'Modo driver';
  }
}

/**
 * A largura com os pontos: no backoffice o suporte precisa distinguir as duas de
 * 80 mm, que o dono vê como "80 mm" e "80 mm (512 pontos)".
 */
export function widthLabel(width: PrinterWidth): string {
  switch (width) {
    case 'mm58':
      return '58 mm (384 pontos)';
    case 'mm80':
      return '80 mm (576 pontos)';
    case 'mm80_512':
      return '80 mm (512 pontos)';
  }
}

export function printerStateLabel(state: PrinterState): string {
  switch (state) {
    case 'ok':
      return 'OK';
    case 'unreachable':
      return 'Inacessível';
    case 'missing':
      return 'Ausente no Windows';
    case 'unknown':
      return 'Sem relato';
  }
}

/** O erro genérico que o painel mostra ao dono, nunca o detalhe técnico. */
export function jobErrorLabel(code: PrintJobErrorCode): string {
  switch (code) {
    case 'printer_unreachable':
    case 'printer_error':
      return 'impressora inacessível';
    case 'printer_unavailable':
      return 'sem impressora ativa para este cupom';
    case 'render_failed':
      return 'falhou ao gerar o cupom';
  }
}

export function ticketLabel(kind: PrintTicketKind, stationName: string | null): string {
  switch (kind) {
    case 'production':
      return stationName ? `Comanda ${stationName}` : 'Comanda';
    case 'checkout':
      return 'Conferência';
    case 'delivery':
      return 'Via de entrega';
    case 'cancellation':
      return 'Aviso de cancelamento';
    case 'test':
      return 'Página de teste';
  }
}

export function eventLabel(type: PrintAgentEventType): string {
  switch (type) {
    case 'paired':
      return 'Pareado';
    case 'connected':
      return 'Conectou';
    case 'disconnected':
      return 'Desconectou';
    case 'token_refused':
      return 'Token recusado';
    case 'credential_reuse_detected':
      return 'Reuso de credencial detectado';
    case 'revoked':
      return 'Revogado';
    case 'suspended':
      return 'Suspenso';
    case 'printer_state_changed':
      return 'Impressora mudou de estado';
    case 'job_failed':
      return 'Cupom falhou';
    case 'late_ack':
      return 'Confirmação tardia';
    case 'protocol_refused':
      return 'Protocolo recusado';
    case 'update_progress':
      return 'Atualização em andamento';
    case 'update_finished':
      return 'Atualização terminou';
    case 'connection_test':
      return 'Teste de conexão';
  }
}

/* ---- Agente de impressão: versões, canal e atualização (change print-agent) ---- */

export function channelLabel(channel: AgentUpdateChannel): string {
  return channel === 'pilot' ? 'Piloto' : 'Estável';
}

/** "do piloto", "do estável": para frases como "Salvar versões do piloto". */
export function channelOf(channel: AgentUpdateChannel): string {
  return channel === 'pilot' ? 'do piloto' : 'do estável';
}

/**
 * Quem alterou. A API devolve só o id do operador: "você" quando é quem está logado, e o
 * começo do id para os demais, que o administrador cruza com a lista de operadores.
 */
export function operatorLabel(operatorId: string | null, currentOperatorId: string | null | undefined): string {
  if (!operatorId) return 'ninguém';
  if (currentOperatorId && operatorId === currentOperatorId) return 'você';
  return `operador ${operatorId.slice(0, 8)}`;
}

/** O que o dono vê no painel em cada nível: aviso dispensável ou diálogo obrigatório. */
export function updateLevelLabel(level: AgentUpdateLevel): string {
  switch (level) {
    case 'mandatory':
      return 'Obrigatória';
    case 'recommended':
      return 'Recomendada';
    case 'none':
      return 'Sem aviso';
  }
}

export function updateStatusLabel(status: AgentUpdateStatus): string {
  switch (status) {
    case 'requested':
      return 'Confirmada, aguardando';
    case 'in_progress':
      return 'Em andamento';
    case 'succeeded':
      return 'Concluída';
    case 'failed':
      return 'Falhou';
    case 'expired':
      return 'Expirou';
  }
}

export function updateModeLabel(mode: AgentUpdateMode): string {
  return mode === 'now' ? 'agora' : 'no fim do expediente';
}

export function updatePhaseLabel(phase: AgentUpdatePhase): string {
  switch (phase) {
    case 'waiting_for_empty_queue':
      return 'esperando a fila esvaziar';
    case 'downloading':
      return 'baixando o instalador';
    case 'applying':
      return 'instalando';
  }
}

/** A explicação do código de falha, que aparece junto do código. */
export function updateFailureLabel(code: AgentUpdateFailureCode): string {
  switch (code) {
    case 'download_failed':
      return 'o instalador não pôde ser baixado';
    case 'verification_failed':
      return 'o instalador baixado não conferiu';
    case 'apply_failed':
      return 'o Windows Installer falhou e a versão anterior voltou';
    case 'version_not_in_feed':
      return 'a versão-alvo não está no feed do canal';
    case 'apply_timeout':
      return 'a versão nova não apareceu em 30 minutos';
  }
}

/** Percentual com uma casa, ou "—" quando não há trabalho na janela. */
export function share(part: number, total: number): string {
  if (total <= 0) return '—';
  return `${new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(
    (part * 100) / total,
  )}%`;
}
